import { Theme, ThemeContext } from "@/contexts/theme/theme.context";
import { keys, StorageUtils } from "@/features/shared/storage/storage.utils";
import { PropsWithChildren, useEffect, useState } from "react";

/**
 * ThemeContextProvider component.
 * @description ThemeContext is used to manage theme state (light/dark/system) with system preference detection
 * and automatic dark class toggling on the html element.
 */
export default function ThemeContextProvider({ children }: PropsWithChildren): React.ReactElement {
  const [theme, setThemeState] = useState<Theme>(() => {
    const storedTheme = StorageUtils.getItem(keys.THEME);
    if (storedTheme === "light" || storedTheme === "dark" || storedTheme === "system") {
      return storedTheme;
    }
    return "system";
  });

  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">(() => {
    if (theme === "system") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return theme;
  });

  const setTheme = (newTheme: Theme): void => {
    setThemeState(newTheme);
    StorageUtils.setItem(keys.THEME, newTheme);
  };

  useEffect(() => {
    const root = document.documentElement;
    const resolved = theme === "system" ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light") : theme;

    setResolvedTheme(resolved);

    if (resolved === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [theme]);

  useEffect(() => {
    if (theme !== "system") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent): void => {
      const root = document.documentElement;
      if (e.matches) {
        root.classList.add("dark");
        setResolvedTheme("dark");
      } else {
        root.classList.remove("dark");
        setResolvedTheme("light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        resolvedTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

